import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../lib/api";
import AnimatedImage from "../../components/AnimatedImage";
import HeroImage from "../../components/HeroImage";
import { useSiteConfig } from "../../context/SiteConfigContext";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";

export default function ProgramDetail() {
    const { slug } = useParams();
    const { config } = useSiteConfig();
    const [program, setProgram] = useState(null);
    const [notFound, setNotFound] = useState(false);
    useEffect(() => {
        setNotFound(false);
        setProgram(null);
        api.get(`/programs/${slug}`).then(({ data }) => setProgram(data)).catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="program-not-found">
                <h1 className="font-display text-3xl mb-4">Program not found</h1>
                <Link to="/programs" className="text-primary underline">Back to programs</Link>
            </div>
        );
    }
    if (!program) return <div className="py-32 text-center text-muted-foreground">Loading…</div>;

    return (
        <div data-testid="program-detail">
            <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
                <HeroImage
                    src={program.image_url}
                    alt={program.title}
                    animation={config?.hero_animation || "ken-burns"}
                    overlay={config?.hero_overlay_style || "dark"}
                    overlayOpacity={config?.hero_overlay_opacity ?? 55}
                />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-14 w-full text-white">
                    <Link to="/programs" className="inline-flex items-center gap-2 text-sm mb-6 opacity-90 hover:opacity-100">
                        <ArrowLeft size={14} /> All Programs
                    </Link>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl">{program.title}</h1>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-2 text-xs uppercase tracking-[0.22em] text-muted-foreground">Overview</div>
                <div className="lg:col-span-10 prose-y">
                    <p className="text-xl leading-relaxed max-w-4xl">{program.overview || program.summary}</p>
                </div>
            </section>

            <section className="bg-muted/40 py-20">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-6">
                        <div className="text-xs uppercase tracking-[0.22em] text-primary mb-4">Key Interventions</div>
                        <h2 className="font-display text-4xl mb-8">How we deliver</h2>
                        <ul className="space-y-3" data-testid="program-interventions">
                            {(program.interventions || []).map((it, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <CheckCircle2 size={18} className="text-primary mt-1 shrink-0" />
                                    <span className="text-base">{it}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="lg:col-span-6">
                        <div className="text-xs uppercase tracking-[0.22em] text-secondary mb-4">Anticipated Impacts</div>
                        <h2 className="font-display text-4xl mb-8">What we change</h2>
                        <ul className="space-y-3" data-testid="program-impacts">
                            {(program.impacts || []).map((it, i) => (
                                <li key={i} className="flex items-start gap-3 border-l-2 border-secondary pl-4 py-1">
                                    <span className="text-base">{it}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
                <div className="bg-card border border-border rounded-sm p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div>
                        <h3 className="font-display text-3xl mb-2">Want to partner on this program?</h3>
                        <p className="text-muted-foreground">Donors, peer NGOs, and government partners — we'd love to hear from you.</p>
                    </div>
                    <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium">
                        Get in touch <ArrowRight size={16} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
